import { useCallback, useEffect, useState } from 'react';
import { PriceSheet } from 'src/Assets/Images';
import {
  ContentDisplayTiles,
  UHConfirmModal,
  Loader,
  UHCalenderComp
} from 'src/components';
import { Box, Divider, makeStyles, Theme, useTheme } from '@material-ui/core';
import { API_SERVICES } from 'src/Services';
import {
  CONFIRM_MODAL,
  CUSTOMER_STATUS,
  HTTP_STATUSES,
  USER_TYPE,
  USER_TYPE_ID
} from 'src/Config/constant';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchVal } from 'src/hooks/useSearchVal';
import { useDebounce } from 'src/hooks/useDebounce';
import { useEdit } from 'src/hooks/useEdit';
import CustomerVendorWalletTable from './CustomerVendorWalletTable';
import VendorCustomerWalletViewModal from './VendorCustomerWalletViewModal';
import CustomerVendorPayoutModal from './CustomerVendorPayoutModal';
import { useNavigate } from 'react-router';
import AddMoneyModal from './AddMoneyModal';
import CustomerVendorPaymentTable from './CustomerVendorPaymentTable';

const useStyles = makeStyles((theme: Theme) => {
  return {
    dialogPaper: {
      width: 847,
      padding: theme.spacing(0, 2, 0, 5)
    }
  };
});

const WalletManagement = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const customerVendorTabValues = [
    {
      heading: t('walletManagement.customersWallet'),
      iconImage: PriceSheet,
      value: 1
    },
    {
      heading: t('walletManagement.vendorsWallet'),
      iconImage: PriceSheet,
      value: 2
    },
    {
      heading: t('walletManagement.customerPayment'),
      iconImage: PriceSheet,
      value: 3
    },
    {
      heading: t('walletManagement.vendorPayout'),
      iconImage: PriceSheet,
      value: 4
    }
  ];

  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<string | number>(
    customerVendorTabValues[0].value
  );
  const [modalOpen, setModalOpen] = useState<any>({ open: false });
  const [tableData, setTableData] = useState<any>([]);
  const [vendorTableData, setVendorTableData] = useState<any>([]);
  const [customerPayoutData, setCustomerPayoutData] = useState<any>([]);
  const [vendorPayoutData, setVendorPayoutData] = useState<any>([]);
  const [confirmModal, setConfirmModal] = useState<any>({
    open: false
  });
  const [viewModal, setViewModal] = useState<any>({ open: false });
  const [regionData, setRegionData] = useState([]);
  const [dateSelect, setDateSelect] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [calendarOpen, setCalendarOpen] = useState<any>({ open: false });
  const [openAddMoneyDialog, setOpenAddMoneyDialog] = useState<any>({
    open: false
  });
  const [orderDetails, setOrderDetails] = useState<any>();
  const navigateTo = useNavigate();
  const initialValue = {
    search: '',
    date: '',
    payout_status: ''
  };

  const edit = useEdit(initialValue);

  const classes = useStyles();
  const { searchValue } = useSearchVal();
  const debValue = useDebounce(searchValue, 2000);

  const handleSelectedTab = (value: number | string) => {
    setSelectedTab(value);
    edit.reset();
  };

  const getAllRegion = async () => {
    try {
      setRegionData([]);
      const response: any =
        await API_SERVICES.regionManagementService.getAllRegion();
      if (response?.data?.statusCode <= HTTP_STATUSES.BAD_REQUEST) {
        setRegionData(response?.data?.result);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setVendorPayoutData([]);
      setTableData([]);
      setVendorTableData([]);
      setCustomerPayoutData([]);
      const params: any = {};
      if (debValue !== '') {
        params.searchString = debValue;
      }
      let searchData = {
        searchString: edit.getValue('search'),
        fromDate: edit.getValue('date')?.split('to')[0],
        toDate: edit.getValue('date')?.split('to')[1],
        ...params
      };
      let searchPayout = {
        searchString: edit.getValue('search'),
        fromDate: edit.getValue('date')?.split('to')[0],
        toDate: edit.getValue('date')?.split('to')[1],
        payout_status: edit.getValue('payout_status'),
        ...params
      };
      const response: any = await Promise.all([
        API_SERVICES.customerVendorListService.getAllCustomer(
          edit.getValue('search') == '' && edit.getValue('date') == ''
            ? params
            : selectedTab == 1 && searchData
        ),
        API_SERVICES.customerVendorListService.getAllVendors(
          edit.getValue('search') == '' && edit.getValue('date') == ''
            ? params
            : selectedTab == 2 && searchData
        ),
        API_SERVICES.WalletManagementService.getAllCustomerPayoutList(
          edit.getValue('search') == '' &&
            edit.getValue('date') == '' &&
            edit.getValue('payout_status') == ''
            ? params
            : selectedTab == 3 && searchPayout
        ),
        API_SERVICES.WalletManagementService.getAllVendorPayoutList(
          edit.getValue('search') == '' &&
            edit.getValue('date') == '' &&
            edit.getValue('payout_status') == ''
            ? params
            : selectedTab == 4 && searchPayout
        )
      ]);

      if (response[0]?.status < HTTP_STATUSES.BAD_REQUEST) {
        if (response[0]?.data?.customerProfile) {
          setTableData(response[0]?.data?.customerProfile);
        }
      }
      if (response[1]?.status < HTTP_STATUSES.BAD_REQUEST) {
        if (response[1]?.data?.vendorProfiles) {
          setVendorTableData(response[1]?.data?.vendorProfiles);
        }
      }
      if (response[2]?.status < HTTP_STATUSES.BAD_REQUEST) {
        if (response[2]?.data?.data?.length) {
          setCustomerPayoutData(response[2]?.data?.data);
        }
      }
      if (response[3]?.status < HTTP_STATUSES.BAD_REQUEST) {
        if (response[3]?.data?.data?.length) {
          setVendorPayoutData(response[3]?.data?.data);
        }
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoading(false);
    }
  }, [debValue, edit.edits]);

  useEffect(() => {
    getAllRegion();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onActionButtonClick = (rowData: any) => {
    setViewModal({ open: true, rowData: rowData });
  };

  const onClickCancelOrder = (rowData: any) => {
    const onCancelClick = () => {
      setConfirmModal({ open: false });
    };
    const onConfirmClick = async () => {
      let updateData = {
        status_id: 4,
        orders: [rowData?.id]
      };
      const response: any = await API_SERVICES.customerOrderService.replace({
        data: updateData,
        successMessage: 'Customer order cancelled successfully!'
      });
      if (response?.status < HTTP_STATUSES.BAD_REQUEST) {
        onCancelClick();
        fetchData();
      }
    };
    let props = {
      color: theme.Colors.redPrimary,
      description: t('customerOrder.cancelCustomerOrder'),
      title: t('customerOrder.cancel'),
      iconType: CONFIRM_MODAL.cancel
    };
    setConfirmModal({ open: true, onConfirmClick, onCancelClick, ...props });
  };

  const handleClickPayout = async (rowData: any, isPayoutButton: any) => {
    try {
      const response: any =
        selectedTab == 3
          ? await API_SERVICES.customerOrderService.getAcceptedOrder(
              rowData.order_no
            )
          : await API_SERVICES.vendorOrderService.getById(rowData?.order_no);
      if (response?.status < HTTP_STATUSES.BAD_REQUEST) {
        if (response?.data?.order) {
          setOrderDetails(response?.data?.order);
          setModalOpen({
            open: true,
            rowData: { ...rowData, ...response?.data?.order },
            isVendor: selectedTab == 4 ? true : false,
            isPayoutButton: isPayoutButton
          });
        }
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
    }
  };

  const onClickViewDetails = (rowData: any, val?: number) => {
    navigateTo('/dashboards/view-details', {
      state: { ...rowData, selectedTab: val }
    });
  };

  const handleChange = useCallback(
    (item) => {
      setDateSelect([item.selection]);

      // If startDate equals endDate and they're the same as the current date,
      // the user might have cleared the selection
      const today = new Date();
      const startDate = item.selection.startDate;
      const endDate = item.selection.endDate;

      if (
        startDate.getDate() === today.getDate() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getFullYear() === today.getFullYear() &&
        endDate.getDate() === today.getDate() &&
        endDate.getMonth() === today.getMonth() &&
        endDate.getFullYear() === today.getFullYear()
      ) {
        // This is likely a reset to today's date, which might indicate clearing
        edit.update({ date: '' });
      }
    },
    [dateSelect]
  );

  const handleClick = () => {
    const fromDate = dateSelect[0].startDate
      .toLocaleDateString('en-US')
      ?.split('/');
    const toDate = dateSelect[0].endDate
      .toLocaleDateString('en-US')
      ?.split('/');
    edit.update({
      date: `${fromDate[1]}/${fromDate[0]}/${fromDate[2]} to ${toDate[1]}/${toDate[0]}/${toDate[2]}`
    });
    setCalendarOpen({ open: false });
  };

  const handleOpenCalendar = () => {
    setCalendarOpen({ open: true });
  };

  const handleClickAddMoney = (rowData: any) => {
    setOpenAddMoneyDialog({ open: true, rowData: rowData, type: 1 });
  };

  const onClickPayout = async (rowData: any, isVendor: boolean) => {
    try {
      let data = {
        user_id: isVendor ? rowData?.vendor_id : rowData.customer_id,
        order_id: rowData?.order_no,
        user_type: isVendor ? USER_TYPE.vendor : USER_TYPE.customer
      };
      const response: any =
        await API_SERVICES.WalletManagementService.adminPayout({ data: data });
      if (response?.status === HTTP_STATUSES.SUCCESS) {
        setModalOpen({ open: false });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickUpdatePaymentStatus = async (rowData: any) => {
    console.log('rowData', rowData);

    const onCancelClick = () => {
      setConfirmModal({ open: false });
    };
    const onConfirmClick = async () => {
      let updateData = {
        payout_status: 'Completed'
      };
      const response: any =
        await API_SERVICES.WalletManagementService.updateOrderTransactionHistory(
          rowData?.id,
          {
            data: updateData,
            successMessage: 'Payment status successfully!'
          }
        );
      if (response?.status < HTTP_STATUSES.BAD_REQUEST) {
        onCancelClick();
        fetchData();
      }
    };
    let props = {
      color: theme.Colors.secondary,
      description: 'Are you sure want to change the status of Payment',
      title: 'Payment Status',
      iconType: CONFIRM_MODAL.accept
    };
    setConfirmModal({ open: true, onConfirmClick, onCancelClick, ...props });
  };

  if (loading) {
    return <Loader />;
  } else {
    return (
      <>
        <ContentDisplayTiles
          displayContent={customerVendorTabValues}
          isTileTypeOrders={true}
          onTabChange={handleSelectedTab}
          tabValue={selectedTab}
        />
        <Divider style={{ backgroundColor: theme.Colors.lightBlue }} />
        <Box sx={{ mt: 3 }}>
          {selectedTab == 2 || selectedTab == 1 ? (
            <CustomerVendorWalletTable
              tableRowData={selectedTab == 2 ? vendorTableData : tableData}
              handleClickViewDetails={onClickViewDetails}
              selectedTab={selectedTab}
              handleChange={(e) => {
                edit.update({ id: e.target.value });
              }}
              edit={edit}
              handleOpenCalendar={handleOpenCalendar}
              handleClickAddMoney={handleClickAddMoney}
            />
          ) : null}
        </Box>
        <Box sx={{ mt: 3 }}>
          {selectedTab == 3 || selectedTab == 4 ? (
            <CustomerVendorPaymentTable
              tableRowData={
                selectedTab == 3 ? customerPayoutData : vendorPayoutData
              }
              handleClickViewDetails={onClickViewDetails}
              handleClickPayout={handleClickPayout}
              selectedTab={selectedTab}
              handleChange={(e) => {
                edit.update({ id: e.target.value });
              }}
              edit={edit}
              handleOpenCalendar={handleOpenCalendar}
              handleClickAddMoney={handleClickAddMoney}
              handleClick={handleClickUpdatePaymentStatus}
            />
          ) : null}
        </Box>
        {modalOpen.open ? (
          <CustomerVendorPayoutModal
            onClose={() => setModalOpen({ open: false })}
            {...modalOpen}
            handleClickPayout={onClickPayout}
          />
        ) : null}
        {viewModal?.open ? (
          <VendorCustomerWalletViewModal
            onClose={() => setViewModal({ open: false })}
            {...viewModal}
          />
        ) : null}
        {calendarOpen.open ? (
          <UHCalenderComp
            onCancelClick={() => {
              // When cancel is clicked, reset everything
              setCalendarOpen({ open: false });
              edit.update({ date: '' });
              // Reset the date selection to today (default state)
              setDateSelect([
                {
                  startDate: new Date(),
                  endDate: new Date(),
                  key: 'selection'
                }
              ]);
            }}
            handleChange={handleChange}
            ranges={dateSelect}
            onConfirmClick={handleClick}
            {...calendarOpen}
          />
        ) : null}
        {openAddMoneyDialog.open && (
          <AddMoneyModal
            onClose={() => setOpenAddMoneyDialog({ open: false })}
            updatedData={fetchData}
            {...openAddMoneyDialog}
          />
        )}
        {confirmModal.open && <UHConfirmModal {...confirmModal} />}
      </>
    );
  }
};

export default WalletManagement;
